<template>
    <div
            v-if="event && !singlePeriod && initialised"
            class="card"
    >
        <booking-summary
                :event="event"
                :booking="booking"
        ></booking-summary>

        <div class="card-body">
            <h2 class="card-text mb-3">
                {{ $t('chooseYourLocker') }}:
            </h2>

            <p v-if="visiblePeriods.length === 0">
                {{ $t('currentlyNoLockersAvailable') }}
            </p>

            <button
                    v-for="period in visiblePeriods"
                    v-bind:key="period.id"
                    class="btn btn-secondary btn-block text-left"
                    @click="setPeriod(period)"
            >
                <img v-if="inPosMode()"
                     src="../assets/img/btn-icon-2x.png"
                     class="mr-1">
                <img v-else
                     src="../assets/img/btn-icon.png"
                     srcset="../assets/img/btn-icon.png 1x, ../assets/img/btn-icon-2x.png 2x, ../assets/img/btn-icon-3x.png 3x"
                     class="mr-1">
                {{ getPeriodText(period, 'name') }}

                <small class="d-block" v-if="getPeriodText(period, 'description')">
                    {{ getPeriodText(period, 'description') }}
                </small>
            </button>

            <div
                v-if="booking.can_select_zone"
                class="btn-group btn-block mt-3"
            >
                <button
                    type="button"
                    class="btn btn-light"
                    @click="resetZone"
                >
                    {{ $t('back') }}
                </button>
            </div>
        </div>

        <locker-company-logo></locker-company-logo>
    </div>
</template>

<script>
    import BookingSummary from '../components/BookingSummary';
    import LockerCompanyLogo from '../components/LockerCompanyLogo';
    import translationMixin from '../mixins/translationMixin';
    import posMixin from '../mixins/posMixin';
    import moment from 'moment';

    const refreshTimeout = 60; // Refresh timeout in seconds

    export default {
        mixins: [translationMixin, posMixin],

        components: {
            BookingSummary,
            LockerCompanyLogo
        },

        props: [
            'event',
            'booking'
        ],

        data() {
            return {
                visiblePeriods: [],
                initialised: false,
                componentBooking: null,
                refreshTimer: null,
                isQR: false,
            }
        },

        computed: {
            /**
             * Just a single period?
             */
            singlePeriod() {
                return this.visiblePeriods.length === 1;
            }
        },

        watch: {
            '$route': 'init',
            event: {
                handler() {
                    this.init();
                },
                deep: true
            }
        },

        /**
         * Initialise component and start refresh.
         */
        created() {
            this.init();
            this.startRefreshTimer();
        },

        /**
         * Stop refresh timer.
         */
        beforeDestroy() {
            this.stopRefreshTimer();
        },

        methods: {
            /**
             * Initialise component, determine visible periods and set booking.
             */
            init() {
                this.isQR = this.$route.query.qr;

                // Can a zone be selected?
                if ((this.event.isolate_zones || this.event.show_zones) && this.booking.preferred_zone === null) {
                    this.booking.can_select_zone = true;
                    this.$router.push('/event/' + this.event.code + '/zones');
                    return;
                }

                this.componentBooking = this.booking;
                this.determineVisiblePeriods();
                this.initialised = true;
            },

            /**
             * Determine visible periods of event.
             *
             * - For POS and QR code only the active periods
             * - Otherwise all future periods
             *
             * Just a single period? Straight to period.
             */
            determineVisiblePeriods() {
                if (this.inPosMode() || this.isQR) {
                    // Check active periods
                    this.visiblePeriods = this.event.periods.filter(period => {
                        return moment(period.assign_at).isBefore() && moment(period.end).isAfter();
                    });
                } else {
                    // All periods
                    this.visiblePeriods = this.event.periods;
                }

                // Just one period? Directly to period
                if (this.singlePeriod) {
                    this.setPeriod(this.visiblePeriods[0]);
                }
            },

            /**
             * Set period and navigate to next step.
             *
             * @param {Object} period
             */
            setPeriod(period) {
                this.componentBooking.period = period;
                if (+this.componentBooking.period_id !== +period.id) {
                    this.componentBooking.period_id = period.id;
                    this.componentBooking.setLinesForLockerSizes(this.event.locker_sizes, period.prices);
                }

                this.$emit('ready', this.componentBooking, false);
                this.$router.push('/event/' + this.event.code + '/lockers');
            },

            /**
             * Reset selected zone and navigate back to zone selection.
             */
            resetZone() {
                this.componentBooking.preferred_zone = null;
                this.$router.push('/event/' + this.event.code + '/zones');
            },

            /**
             * Start timer for refreshing visible periods.
             */
            startRefreshTimer() {
                this.refreshTimer = setInterval(this.determineVisiblePeriods, refreshTimeout * 1000);
            },

            /**
             * Stop timer for refreshing visible periods.
             */
            stopRefreshTimer() {
                if (this.refreshTimer === null) {
                    return;
                }

                clearInterval(this.refreshTimer);

                this.refreshTimer = null;
            }
        }
    }
</script>
